import { create } from 'zustand'
import { dbgEvent } from '../lib/debug'

/**
 * File d'attente d'envoi, FIFO **par session** (#10587).
 *
 * Quand un tour est en cours (agent occupé), l'envoi n'est plus refusé : le message est
 * empilé ici, puis flushé par `InputBar` à la fin du tour courant (transition du statut de
 * session vers un état terminal idle/error/closed). Voir `InputBar.tsx` (effet de flush).
 *
 * Pourquoi la file est CLIENT-side et non serveur : le daemon (`handleSend`) pousse un `send`
 * dans le flux SDK **vivant** sans le rejeter — un 2e message envoyé mid-turn s'injecterait
 * dans le MÊME tour SDK. Sérialiser côté client (n'envoyer le suivant qu'après la fin du tour)
 * est la seule façon de garantir un tour propre par message.
 */

export interface QueuedMessage {
  content: string
  attachmentIds: string[]
}

interface SendQueueState {
  /** Par sessionId : les messages en attente, dans l'ordre d'arrivée (FIFO). */
  queues: Record<string, QueuedMessage[]>

  /** Empile un message en fin de file pour la session. */
  enqueue: (sessionId: string, message: QueuedMessage) => void
  /** Défile et renvoie le message en TÊTE de file (FIFO), ou undefined si vide. */
  shift: (sessionId: string) => QueuedMessage | undefined
  /** Nombre de messages en attente pour la session. */
  count: (sessionId: string) => number
  /** Vide entièrement la file d'une session. */
  clear: (sessionId: string) => void
}

export const useSendQueueStore = create<SendQueueState>((set, get) => ({
  queues: {},

  enqueue: (sessionId, message) => {
    set(state => {
      const current = state.queues[sessionId] ?? []
      const nextQueue = [...current, message]
      dbgEvent('Zustand', 'sendQueue.enqueue', { sessionId: sessionId.slice(0, 8), size: nextQueue.length })
      return { queues: { ...state.queues, [sessionId]: nextQueue } }
    })
  },

  shift: (sessionId) => {
    const current = get().queues[sessionId] ?? []
    if (current.length === 0) return undefined
    const [head, ...rest] = current
    set(state => {
      dbgEvent('Zustand', 'sendQueue.shift', { sessionId: sessionId.slice(0, 8), remaining: rest.length })
      return { queues: { ...state.queues, [sessionId]: rest } }
    })
    return head
  },

  count: (sessionId) => (get().queues[sessionId]?.length ?? 0),

  clear: (sessionId) => {
    set(state => {
      if (!state.queues[sessionId] || state.queues[sessionId].length === 0) return state
      dbgEvent('Zustand', 'sendQueue.clear', { sessionId: sessionId.slice(0, 8) })
      const next = { ...state.queues }
      delete next[sessionId]
      return { queues: next }
    })
  },
}))
